import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, ImageIcon } from "lucide-react";
import { api, assetUrl, formatApiErrorDetail } from "../../lib/api";

export const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-xs font-bold tracking-widest uppercase text-gray-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export const Modal = ({ title, onClose, children, testId = "admin-modal" }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
    <div data-testid={testId} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">{title}</h3>
        <button onClick={onClose} data-testid="form-close" className="text-gray-400 hover:text-gray-900 text-2xl leading-none">×</button>
      </div>
      {children}
    </div>
  </div>
);

export const FormActions = ({ onClose, saving }) => (
  <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
    <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-lg border border-gray-300 font-semibold">Cancelar</button>
    <button data-testid="form-save" disabled={saving} className="px-5 py-2.5 rounded-lg bg-[#0A3977] text-white font-bold disabled:opacity-60 inline-flex items-center gap-2">
      {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
    </button>
  </div>
);

export const ImageUploader = ({ value, onChange, label = "Foto" }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      onChange(res.url);
      toast.success("Foto subida");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div>
      <label className="text-xs font-bold tracking-widest uppercase text-gray-500">{label}</label>
      <div className="mt-1 flex gap-4 items-start">
        <div className="w-28 h-28 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
          {value ? (
            <img data-testid="form-image-preview" src={assetUrl(value)} alt="Vista previa" className="w-full h-full object-cover" />
          ) : <ImageIcon className="w-8 h-8 text-gray-300" />}
        </div>
        <div className="flex-1 space-y-2">
          <input ref={inputRef} data-testid="form-image-file" type="file" accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" data-testid="form-image-upload-btn" disabled={uploading} onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0A3977] text-white text-sm font-bold hover:bg-[#0067B1] disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Subiendo..." : "Subir foto"}
          </button>
          <p className="text-xs text-gray-400">JPG, PNG o WEBP · máx. 5 MB</p>
          <input data-testid="form-image" value={value} onChange={(e) => onChange(e.target.value)} placeholder="o pegá una URL de imagen" className="inp text-xs" />
        </div>
      </div>
    </div>
  );
};
